import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AppDivider,
  AppFlexContainer,
  AppFlexItem,
  AppParagraph,
  AppProgressBar,
  AppSegment,
} from 'client/ui-building-blocks'

import { useGetQuestionCategories } from 'client/api'
import { TGetUserData } from 'client/api/useGetUser/useGetUser'

import ResultsTypeSelect from '../ResultsTypeSelect/results-type-select'

type Props = {
  getUserData: TGetUserData
}

const ResultsByCategory = ({ getUserData }: Props) => {
  const { i18n } = useTranslation()

  const [resultsType, setResultsType] = useState<'top' | 'popular'>('popular')

  const { data: questionCategories } = useGetQuestionCategories({})

  const onResultsTypeChange = useCallback(e => {
    setResultsType(e.target.value)
  }, [])

  const QUESTION_CATEGORIES = useMemo(() => {
    if (!questionCategories) {
      return null
    }
    const obj = {}
    questionCategories[i18n.language.toLowerCase()].forEach(c => {
      obj[c.key] = c
    })
    return obj
  }, [questionCategories])

  const sortAnswerStatsByTop = (a, b) =>
    b.correctCount / b.count - a.correctCount / a.count ||
    b.count - a.count ||
    QUESTION_CATEGORIES[a.category].text.localeCompare(
      QUESTION_CATEGORIES[b.category].text
    )

  const sortAnswerStatsByPopular = (a, b) =>
    b.count - a.count ||
    b.correctCount - a.correctCount ||
    QUESTION_CATEGORIES[a.category].text.localeCompare(
      QUESTION_CATEGORIES[b.category].text
    )

  const Row = (
    {
      category,
      count,
      correctCount,
    }: { category: string; count: number; correctCount: number },
    i: number
  ) => (
    <>
      <AppFlexItem>
        <AppParagraph
          size="small"
          text={`${QUESTION_CATEGORIES[category].text} (${correctCount}/${count})`}
        />
      </AppFlexItem>
      <AppFlexItem key={i} width="100%">
        <AppProgressBar
          total={count}
          value={correctCount}
          progress="percent"
          indicating={false}
        />
      </AppFlexItem>
    </>
  )

  const { answersStats } = getUserData.data.count

  if (QUESTION_CATEGORIES && answersStats.length > 0) {
    return (
      <>
        <AppDivider size="large" />
        <AppSegment
          headerProps={{ text: 'Your results by category', marginBottom: 'small' }}
        >
          <ResultsTypeSelect value={resultsType} onChange={onResultsTypeChange} />
          <AppDivider size="small" />
          <AppFlexContainer flexFlow="column" rowGap="7px">
            {answersStats
              .sort(
                resultsType === 'top' ? sortAnswerStatsByTop : sortAnswerStatsByPopular
              )
              .map(Row)}
          </AppFlexContainer>
        </AppSegment>
      </>
    )
  }

  return null
}

export default ResultsByCategory
